import React, {
  FC,
  UIEvent,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { CircularProgress } from '@material-ui/core';
import cx from 'classnames';

import { ReactComponent as MoveDown } from 'assets/icons/move-down.svg';
import { ReactComponent as EmptySlotCalendar } from 'assets/icons/empty-slot-calendar.svg';
import useIsMobile from 'hooks/useIsMobile';
import { ITimeSlot } from 'interfaces/timeslotType';
import { IWeeklyTimeSlotsClinicData } from 'services/APIs/getClinicAvailability';

import DayViewClinic from './DayViewClinic';
import DayViewPractitioner from './DayViewPractitioner';
import convertDateBlocks from './utils/convertDateBlocks';
import reducer, { VIEW_ACTION, initialState } from './reducer';

import css from './DayView.module.scss';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import axios from 'axios';
import moment from 'moment';
import getNearestAvailForClinic from './utils/getNearestAvailForClinic';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import { useLocation } from 'react-router-dom';
import { parseQueryUrl } from 'utils/parseQueryUrl';
import { useAppSelector } from 'redux/reduxHooks';
import {
  convertToDate,
  getAllDatesWithinYearFromDate,
  getFilterDates,
} from 'utils/dates';

interface DayViewProps {
  isLoading: boolean;
  date: string;
  onDateUpdate: (newDate: string) => void;
  isClinicSearch: boolean;
  clinicData?: IWeeklyTimeSlotsClinicData;
  timeSlots?: ITimeSlot[];
  onClickClinicTimeSlot?: (
    timeSlot: number,
    appointmentDate: string,
    practitionerIds: string[]
  ) => void;
  onClickPractitionerTimeSlot?: (
    timeSlot: number,
    appointmentDate: string
  ) => void;
  headerClassName?: string;
  bodyClassName?: string;
  blurBottomBgClassName?: string;
  blurTopBgClassName?: string;
  iconClassName?: string;
  clinicId?: string;
  practitionerId?: string;
  serviceId: string;
  isInitialEmptySlot: boolean;
  dentistId?: string;
}

const DayView: FC<DayViewProps> = ({
  isLoading,
  date,
  isClinicSearch,
  clinicData,
  timeSlots,
  onClickClinicTimeSlot,
  onClickPractitionerTimeSlot,
  headerClassName,
  bodyClassName,
  blurTopBgClassName,
  blurBottomBgClassName,
  iconClassName,
  clinicId,
  practitionerId,
  serviceId,
  onDateUpdate,
  isInitialEmptySlot,
  dentistId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const isLoadedInsideIframe = useIsLoadedInsideIframe();
  const isMobile = useIsMobile();
  const location = useLocation();

  const { codeUrl, serviceSlugNameUrl } = parseQueryUrl(location);

  const selectedLastInsurance = useAppSelector(
    (state) => state.bookingFormSlice.selectedLastInsurance
  );

  const lastInsuranceCarrierId = selectedLastInsurance?.insuranceCarrierId;

  const baseIconClassName = cx({
    [css['animate-icon']]: true,
    [css['animate-icon-remove']]: !state.displayMoreIcon,
    [css['animate-icon-iframe']]: isLoadedInsideIframe,
  });

  const bodyContainerClassName = cx({
    [css['grid-container-body']]: true,
    [css['grid-container-body-iframe']]: isLoadedInsideIframe,
  });

  const blurTopClassName = cx({
    [css['blur-bg-top']]: true,
    [css['blur-bg-top-iframe']]: isLoadedInsideIframe,
  });

  const blurBottomClassName = cx({
    [css['blur-bg-bottom']]: true,
    [css['blur-bg-bottom-iframe']]: isLoadedInsideIframe,
  });

  const isAllAvailableBlocksEmpty = clinicData?.doctors?.every((doctor) =>
    doctor.availableBlocks.every((block) => block.blocks.length === 0)
  );

  const isClinicDataEmpty = isClinicSearch && isAllAvailableBlocksEmpty;
  const isTimeSlotsEmpty = !isClinicSearch && timeSlots?.length === 0;
  const dataBlocks = convertDateBlocks(date, isMobile);

  const [nextAvailabilityDate, setNextAvailabilityDate] = useState('');
  const [isLoadingNextAvailabilityDate, setIsLoadingNextAvailabilityDate] =
    useState(false);
  const [searchingDateRanges, setSearchingDateRanges] = useState<
    string[] | null
  >(null);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [currentSearchingRange, setCurrentSearchingRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [searchStartMonth, setSearchStartMonth] = useState(0);
  const cancelTokenSourceRef = useRef<ReturnType<
    typeof axios.CancelToken.source
  > | null>(null);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    const getNearestAvailabilitySlots = async () => {
      if (isLoading) return;

      const searchMonths = async () => {
        for (let month = 0; month < 3; month++) {
          if (cancelTokenSource.token.reason) {
            return;
          }

          const totalMonth = searchStartMonth + month;

          const startDate = moment(getFilterDates(date)[1], 'YYYY-MM-DD')
            .add(totalMonth, 'months')
            .format('YYYY-MM-DD');

          const endDate = moment(startDate, 'YYYY-MM-DD')
            .add(1, 'months')
            .subtract(1, 'days')
            .format('YYYY-MM-DD');

          setCurrentSearchingRange({
            from: startDate,
            to: endDate,
          });

          const body = {
            date: startDate,
            serviceId,
            ...(codeUrl && { code: codeUrl }),
            ...(serviceSlugNameUrl && { serviceSlugName: serviceSlugNameUrl }),
            ...(dentistId && { dentistId }),
            ...(lastInsuranceCarrierId && {
              insuranceId: lastInsuranceCarrierId,
            }),
          };

          try {
            if (isClinicDataEmpty) {
              const response = await axios.post(
                `/clinics/${clinicId}/next-availability`,
                body,
                {
                  baseURL: process.env.REACT_APP_AXIOS_BE_URL,
                  cancelToken: cancelTokenSource.token,
                }
              );

              if (response.data.doctors.length > 0) {
                const nextAvail = getNearestAvailForClinic(
                  response.data.doctors
                );

                if (isInitialEmptySlot) {
                  if (nextAvail.nextAvailabilityDate !== null) {
                    onDateUpdate(nextAvail.nextAvailabilityDate!);
                  }
                } else {
                  setNextAvailabilityDate(nextAvail.nextAvailabilityDate!);
                }

                setSearchingDateRanges(response.data.searchingDateRanges);
                setSearchCompleted(true);
                return;
              }
            } else if (
              isTimeSlotsEmpty &&
              practitionerId &&
              practitionerId !== 'all'
            ) {
              const response = await axios.post(
                `/practitioners/${practitionerId}/next-availability`,
                body,
                {
                  baseURL: process.env.REACT_APP_AXIOS_BE_URL,
                  cancelToken: cancelTokenSource.token,
                }
              );

              if (response.data?.nextAvailabilityDate) {
                setNextAvailabilityDate(response.data.nextAvailabilityDate);
                setSearchingDateRanges(response.data.searchingDateRanges);
                setSearchCompleted(true);
                return;
              }
            }

            // Add small delay between requests to prevent hammering the server
            await new Promise((resolve) => setTimeout(resolve, 300));
          } catch (error) {
            if (axios.isCancel(error)) {
              return;
            }
          }
        }

        const finalEndDate = moment(getFilterDates(date)[1], 'YYYY-MM-DD')
          .add(searchStartMonth + 2, 'months')
          .add(1, 'months')
          .subtract(1, 'days')
          .format('YYYY-MM-DD');

        setSearchingDateRanges([finalEndDate]);
        setSearchCompleted(true);
      };

      setIsLoadingNextAvailabilityDate(true);
      setSearchCompleted(false);
      await searchMonths();
      setIsLoadingNextAvailabilityDate(false);
    };

    getNearestAvailabilitySlots();

    return () => {
      cancelTokenSource.cancel();
    };
  }, [
    clinicId,
    codeUrl,
    date,
    dentistId,
    isClinicDataEmpty,
    isInitialEmptySlot,
    isLoading,
    isTimeSlotsEmpty,
    onDateUpdate,
    practitionerId,
    serviceId,
    serviceSlugNameUrl,
    searchStartMonth,
    lastInsuranceCarrierId,
  ]);

  useEffect(() => {
    if (isLoading || isLoadingNextAvailabilityDate) {
      return;
    }
    const element = containerRef.current!;
    const isOverflow = element.scrollHeight > element.clientHeight;

    dispatch({
      type: isOverflow ? VIEW_ACTION.SCROLL_TO_TOP : VIEW_ACTION.RESET,
    });
  }, [isLoading, isLoadingNextAvailabilityDate]);

  const renderContent = (
    data: { timeDate: string; labelDayOfWeek: string; labelDayOfMonth: string },
    index: number
  ) => {
    if (isClinicSearch && clinicData && onClickClinicTimeSlot) {
      return (
        <DayViewClinic
          date={data.timeDate}
          clinicData={clinicData}
          onClickTimeSlot={(block: number, practitionerIds: string[]) => {
            onClickClinicTimeSlot(block, data.timeDate, practitionerIds);
          }}
        />
      );
    }
    if (!isClinicSearch && timeSlots && onClickPractitionerTimeSlot) {
      return (
        <DayViewPractitioner
          timeSlots={timeSlots}
          currentIndex={index}
          onClickTimeSlot={(block: number) =>
            onClickPractitionerTimeSlot(block, data.timeDate)
          }
        />
      );
    }
  };

  const handleOnScroll = (e: UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop === 0) {
      dispatch({ type: VIEW_ACTION.SCROLL_TO_TOP });
    } else {
      dispatch({ type: VIEW_ACTION.IS_SCROLLING });
    }
    if (
      Math.floor(target.scrollHeight - target.scrollTop) <= target.clientHeight
    ) {
      dispatch({ type: VIEW_ACTION.SCROLL_TO_BOTTOM });
    }
  };

  const handleGetNextAvailability = () => {
    setNextAvailabilityDate('');
    onDateUpdate(nextAvailabilityDate);
  };

  const handleRetrySearch = () => {
    setNextAvailabilityDate('');
    setSearchCompleted(false);
    setCurrentSearchingRange(null);
    setSearchStartMonth((prev) => prev + 3);
  };

  const renderNextAvailabilityDateWhenNotFound = () => {
    const hasSearchingDateRanges =
      searchingDateRanges && searchingDateRanges?.length > 0;

    const fromDate = moment(convertToDate(date), 'YYYY-MM-DD').format(
      'MMM DD, YYYY'
    );

    const toDate = hasSearchingDateRanges
      ? moment(searchingDateRanges![searchingDateRanges!.length - 1]).format(
          'MMM DD, YYYY'
        )
      : '';

    const searchedRangeText = hasSearchingDateRanges
      ? `${fromDate} - ${toDate}`
      : '';

    return (
      <>
        <EmptySlotCalendar />
        <span>{`No availabilities ${
          hasSearchingDateRanges ? `from ${fromDate} to ${toDate}` : ''
        }`}</span>

        {searchCompleted && searchedRangeText && (
          <div className={css['search-again-section']}>
            <DentalButton
              variant="outlined"
              className={css['next-availability-button']}
              onClick={handleRetrySearch}
            >
              Find next availabilities within 3 months
            </DentalButton>
          </div>
        )}
      </>
    );
  };

  const renderLoadingProgress = () => {
    return (
      <div className={css['searching-container']}>
        <CircularProgress className={css['searching-progress']} size={50} />
        <span className={css['searching-text']}>Loading availabilities...</span>
      </div>
    );
  };

  const renderSearchingProgress = () => {
    const fromDate = currentSearchingRange
      ? moment(currentSearchingRange.from).format('MMM DD, YYYY')
      : '';
    const toDate = currentSearchingRange
      ? moment(currentSearchingRange.to).format('MMM DD, YYYY')
      : '';

    return (
      <div className={css['searching-container']}>
        <CircularProgress className={css['searching-progress']} size={50} />
        <span className={css['searching-text']}>
          Searching for availabilities...
        </span>
        {currentSearchingRange && (
          <span className={css['searching-range']}>
            {`${fromDate} - ${toDate}`}
          </span>
        )}
      </div>
    );
  };

  const renderNextAvailabilityDateWhenFound = () => {
    let nextAvailText = '';

    const isSameCurrentYear = moment(nextAvailabilityDate, 'YYYY-MM-DD').isSame(
      moment(),
      'years'
    );
    if (isSameCurrentYear) {
      nextAvailText = moment(nextAvailabilityDate).format('MMM DD');
    } else {
      nextAvailText = moment(nextAvailabilityDate).format('MMM DD, YYYY');
    }

    return (
      <>
        <EmptySlotCalendar />
        <DentalButton
          variant="outlined"
          className={css['next-availability-button']}
          onClick={handleGetNextAvailability}
        >
          View Next Availabilities on {nextAvailText}
        </DentalButton>
      </>
    );
  };

  const renderLabelDay = (
    dataBlocks: {
      timeDate: string;
      labelDayOfWeek: string;
      labelDayOfMonth: string;
    }[]
  ) => {
    const currentDate = moment().format('YYYY-MM-DD');

    const allDatesWithinAYear = getAllDatesWithinYearFromDate(currentDate);

    return dataBlocks.map((data) => {
      const isDisabled = !allDatesWithinAYear.includes(data.timeDate);

      return (
        <div
          key={`${data.timeDate}`}
          className={cx({
            [css['grid-column']]: true,
            [css['grid-column-disabled']]: isDisabled,
          })}
        >
          <span className={css['label-sm']}>{data.labelDayOfWeek}</span>
          <span className={css['label-md']}>{data.labelDayOfMonth}</span>
        </div>
      );
    });
  };

  const isEmptySlotDisplayed = isClinicDataEmpty || isTimeSlotsEmpty;

  if (isLoading) {
    return renderLoadingProgress();
  }

  if (
    isLoadingNextAvailabilityDate &&
    !searchCompleted &&
    isEmptySlotDisplayed
  ) {
    return renderSearchingProgress();
  }

  return (
    <>
      <div className={`${css['grid-container-header']} ${headerClassName}`}>
        {renderLabelDay(dataBlocks)}
        {state.isScrolling && (
          <div className={`${blurTopClassName} ${blurTopBgClassName}`} />
        )}
      </div>
      <div
        className={`${bodyContainerClassName} ${bodyClassName}`}
        onScroll={handleOnScroll}
        ref={containerRef}
      >
        {isEmptySlotDisplayed && !isLoadingNextAvailabilityDate ? (
          <div className={css['emptyslot-container']}>
            {nextAvailabilityDate
              ? renderNextAvailabilityDateWhenFound()
              : renderNextAvailabilityDateWhenNotFound()}
          </div>
        ) : (
          dataBlocks.map((data, index) => (
            <div key={data.timeDate} className={css['column-container']}>
              {renderContent(data, index)}
            </div>
          ))
        )}
        <MoveDown className={`${baseIconClassName} ${iconClassName}`} />
      </div>
      {state.displayBlurBottom && (
        <div className={`${blurBottomClassName} ${blurBottomBgClassName}`} />
      )}
    </>
  );
};

export default DayView;
